import { ReactNode } from "react";
import { Trans, useTranslation } from "react-i18next";

import { InfoCircle, Logout } from "@/assets/icons/ds";
import { Button } from "@/components/Interface/Button/Button.tsx";
import { Notification } from "@/components/Interface/Notification/Notification.tsx";
import { ProfileCard } from "@/components/Layout/BaseLayouts/ProfileCard/ProfileCard.tsx";

/**
 * Which discount status the group is in. The design shows one notification per
 * combination of "group is full" and "maximum discount reached"; the group
 * without an eligible Flat Mobile yet shows none at all and gets the rates
 * section instead.
 */
export type FriendsGroupStatus =
  | "none"
  | "upsell"
  | "maxDiscount"
  | "full"
  | "fullMaxDiscount";

export interface FriendsGroupCardProps {
  dataTestid?: string;
  title: string;
  status: FriendsGroupStatus;
  /** Current group discount in percent. */
  discount: number;
  /** Discount of the next level in percent, shown by the upsell status. */
  nextDiscount: number;
  /** How many members or devices are missing for the next level. */
  missingForNextLevel: number;
  onShowInstructions: () => void;
  onLeaveGroup: () => void;
}

const statusI18nKeys: Record<
  Exclude<FriendsGroupStatus, "none">,
  { title: string; description: string }
> = {
  upsell: { title: "status.discountTitle", description: "status.nextLevel" },
  maxDiscount: {
    title: "status.discountTitle",
    description: "status.maxDiscount",
  },
  full: { title: "status.fullTitle", description: "status.fullDescription" },
  fullMaxDiscount: {
    title: "status.fullMaxTitle",
    description: "status.fullMaxDescription",
  },
};

export const FriendsGroupCard = ({
  dataTestid,
  title,
  status,
  discount,
  nextDiscount,
  missingForNextLevel,
  onShowInstructions,
  onLeaveGroup,
}: FriendsGroupCardProps) => {
  const { t } = useTranslation("portal", {
    keyPrefix: "friendsWithBenefits.groupOverview",
  });

  const highlight = <span className="font-semibold text-foreground-info" />;

  const renderStatusText = (i18nKey: string): ReactNode => (
    <Trans
      t={t}
      i18nKey={i18nKey as never}
      count={missingForNextLevel}
      components={[highlight, highlight, highlight]}
      values={{
        percentage: `${discount}%`,
        count: missingForNextLevel,
        nextPercentage: `${nextDiscount}%`,
      }}
    />
  );

  return (
    <ProfileCard
      dataTestid={dataTestid}
      title={title}
      variant="ivory"
      maxWidth="none"
      stickyHeader={false}
      stackActionsOnMobile
      actions={
        <>
          <Button
            dataTestid="fwb-how-it-works"
            variant="tertiary"
            size="sm"
            icon={<InfoCircle />}
            onClick={onShowInstructions}
          >
            {t("howItWorks")}
          </Button>
          <Button
            dataTestid="fwb-leave-group"
            variant="secondary"
            size="sm"
            icon={<Logout />}
            onClick={onLeaveGroup}
          >
            {t("leaveTribe")}
          </Button>
        </>
      }
    >
      {status !== "none" && (
        <Notification
          dataTestid="fwb-group-status"
          title={renderStatusText(statusI18nKeys[status].title)}
          description={renderStatusText(statusI18nKeys[status].description)}
        />
      )}
    </ProfileCard>
  );
};
